import {useParams, useSearchParams} from 'react-router-dom';
import RichList, {RichListItem} from '../components/RichList';
import {Box, Button, CardContent, Grid, IconButton, LinearProgress, Snackbar, Stack, Typography} from '@mui/material';
import {useAddress} from '../queries';
import QueryProcessor from '../components/QueryProcessor';
import {TabbedMainCard} from '../components/MainCard';
import {showAmount, SmartLink} from '../abelcommon';
import React from "react";
import ErrorCard from "../components/ErrorCard";
import useAddressTxs from "../queries/useAddressTxs";
import PaginationCard from "../components/PaginationCard";
import TxList from "../components/TxList";
import {messageForPrivacyLevel} from '../components/PrivacyLevel';
import ContentCopy from "@mui/icons-material/ContentCopy";
import axios from "axios";
import {environment} from "../config/environment";

function useAddressWithPrecheck(address?: string) {
    if (!address || !address.startsWith('0xabe')) {
        throw new Error('Illegal address.');
    }
    return useAddress(address);
}

function AddressOverview({d}: { d: any }) {
    const {address} = useParams();
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState<string>('');
    const [fullAddressObtained, setFullAddressObtained] = React.useState(false);
    const [fullAddress, setFullAddress] = React.useState<string>('');
    return (
        <RichList sx={{width: 1, mt: -2}}>
            <RichListItem
                title={'Address (Short)'}
                help={'The 66-byte or 68-byte short address in which the last 32 bytes are unknown to the public.'}
            >
                <Stack direction='row' spacing={1}>
                    <Box>
                        <Typography variant={'body2'} sx={{wordWrap: 'anywhere'}}>
                            {d.shortAddress ? d.shortAddress : `${address + '*'.repeat(64)}`}
                        </Typography>
                    </Box>
                    {d.shortAddress && (
                        <Box alignSelf={'center'} onClick={async () => {
                            await navigator.clipboard.writeText(d.shortAddress);
                            setMessage(`short address (${d.shortAddress.length} characters) copied to clipboard.`);
                            setOpen(true);
                            return;
                        }}>
                            <IconButton
                                color="primary"
                                sx={{
                                    background: 'inherit',
                                    padding: '2px',
                                }}
                            >
                                <ContentCopy sx={{
                                    fontSize: '15px'
                                }}/>
                            </IconButton>
                        </Box>
                    )}
                </Stack>
            </RichListItem>
            {d.shortAddress && (<RichListItem
                title={'Address (Full)'}
                help={'The full address from somewhere corresponding short address.'}
            >
                <Button variant={'outlined'}
                        size={'small'}
                        onClick={async () => {
                            if (fullAddressObtained) {
                                await navigator.clipboard.writeText(fullAddress);
                                setMessage(`full address (${fullAddress.length} characters) copied to clipboard.`);
                                setOpen(true);
                                return;
                            }
                            const resp = await axios.get(`${environment.apiBase}/address/verbose?shortAddress=${d.shortAddress}`);
                            if (resp.data.type !== 'address-verbose') {
                                throw new Error('Failed to obtain full address.');
                            }

                            setFullAddress(resp.data.payload.abelAddress)
                            setFullAddressObtained(true)
                            return;
                        }}>
                    {fullAddressObtained ? 'copy full address' : 'obtain full address'}
                </Button>
            </RichListItem>)}

            <RichListItem
                title={'Privacy Level'}
                help={'The 66-byte or 68-byte short address in which the last 32 bytes are unknown to the public.'}
            >
                <Typography variant={'body2'}>
                    {messageForPrivacyLevel(d.privacyLevel)}
                </Typography>
            </RichListItem>

            <RichListItem title={'Transactions'} help={'The total number of transactions involving this address.'}>
                <Typography variant={'body2'}>{d.txn}</Typography>
            </RichListItem>

            <RichListItem title={'First Block'}
                          help={'The height of the earliest block where this address discovered.'}>
                <SmartLink to={`/block/${d.firstBlockHeight}`}>
                    <Typography variant={'body2'}>{d.firstBlockHeight}</Typography>
                </SmartLink>
            </RichListItem>

            <RichListItem title={'Last Mined Block'}
                          help={'The height of the latest block where this address discovered.'}>
                <SmartLink to={`/block/${d.lastBlockHeight}`}>
                    <Typography variant={'body2'}>{d.lastBlockHeight}</Typography>
                </SmartLink>
            </RichListItem>
            <RichListItem title={'Total Received'} help={'The total amount received may be by this address.'}>
                <Typography variant={'body2'}>{showAmount(d.privacyLevel, d.totalReceived)}</Typography>
            </RichListItem>
            <RichListItem title={'Total Sent'} help={'The total amount sent may be by this address.'}>
                <Typography variant={'body2'}>{showAmount(d.privacyLevel, d.totalSent)}</Typography>
            </RichListItem>
            <RichListItem title={'Balance'} help={'The balance of this address.'}>
                <Typography variant={'body2'}>{showAmount(d.privacyLevel, d.balance)}</Typography>
            </RichListItem>
            <Snackbar
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                }}
                open={open}
                onClose={() => {
                    setOpen(false);
                }}
                message={message}
                autoHideDuration={1200}
            />
        </RichList>
    )
        ;
}

interface ViewAddressTxsProps {
    address: string;
    totalTxs: number;
    pageSize: number;
    pageNo: number;
}

function ViewAddressTxs({address, totalTxs, pageSize, pageNo}: ViewAddressTxsProps) {
    const addressTxSeqNoBegin = (pageNo - 1) * pageSize;
    const addressTxSeqNoEnd = addressTxSeqNoBegin + pageSize;

    return (
        <PaginationCard totalItems={totalTxs}
                        pageSize={pageSize} pageNo={pageNo}>
            <ViewAddressTxsContent
                address={address}
                totalTxs={totalTxs}
                addressTxSeqNoBegin={addressTxSeqNoBegin}
                addressTxSeqNotEnd={addressTxSeqNoEnd}
            />
        </PaginationCard>
    );
}


interface ViewAddressTxsContentProps {
    address: string,
    totalTxs: number,
    addressTxSeqNoBegin: number;
    addressTxSeqNotEnd: number;
}

function ViewAddressTxsContent({
                                   address,
                                   totalTxs,
                                   addressTxSeqNoBegin,
                                   addressTxSeqNotEnd
                               }: ViewAddressTxsContentProps) {
    const fetchCount = 100;
    const startForAddressTxSeqnoBegin = Math.floor(addressTxSeqNoBegin / fetchCount) * fetchCount;
    const startForAddressTxSeqnoEnd = Math.floor(addressTxSeqNotEnd / fetchCount) * fetchCount;

    const qs = [
        useAddressTxs(address, startForAddressTxSeqnoBegin, fetchCount, {
            enabled: startForAddressTxSeqnoBegin !== startForAddressTxSeqnoEnd,
        }),
        useAddressTxs(address, startForAddressTxSeqnoEnd, fetchCount),
    ];

    if (qs.some((q) => q.isLoading) || qs.some((q) => q.isRefetching)) {
        return <LinearProgress color={'warning'}/>;
    }

    if (qs.some((q) => q.isError) || qs.some((q) => !q.isSuccess)) {
        return (
            <ErrorCard enableGoBack={true}>
                <Typography variant={'h4'}>Failed fetching transaction data for address.</Typography>
            </ErrorCard>
        );
    }

    let data =
        startForAddressTxSeqnoBegin === startForAddressTxSeqnoEnd ? qs[0].data.payload : [...qs[0].data.payload, ...qs[1].data.payload];
    const dataBegin = addressTxSeqNoBegin - startForAddressTxSeqnoBegin;
    const dataEnd = dataBegin + (addressTxSeqNotEnd - addressTxSeqNoBegin);
    data = data.filter((d: any, idx: number) => dataBegin <= idx && idx < dataEnd);

    return <TxList address={address} data={data}/>;
}

const allowedPageSizes = [10, 25, 50, 100];
const ViewAddress = () => {
    let {address} = useParams();
    if (address === undefined) {
        throw new Error('Illegal address.');
    }
    if (address === '') {
        throw new Error('Illegal address.');
    }
    if (address && !address.startsWith('0x')) {
        address = '0x' + address;
    }

    const [searchParams, setSearchParams] = useSearchParams();

    let pageSize = Number(searchParams.get('ps') ?? 15);
    if (isNaN(pageSize) || !allowedPageSizes.includes(pageSize)) {
        pageSize = 15;
    }

    let pageNo = Number(searchParams.get('p') ?? 1);
    if (isNaN(pageNo) || pageNo < 1) {
        pageNo = 1;
    }
    let selectedTab = Number(searchParams.get('tab') ?? 0);

    return (
        <QueryProcessor
            query={useAddressWithPrecheck(address)}
            dataValidator={(data: any) => data.type === 'address'}
            renderer={(data: any) => (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography component={'span'} variant={'h2'}>
                            Address Details
                        </Typography>
                        <Typography>
                            {`${data.payload.shortAddress || address + '*'.repeat(64)}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TabbedMainCard
                            labels={['Overview', 'Transactions']}
                            initialTab={selectedTab}
                            switchFn={(tabIndex: number) => {
                                const newSearchParams = new URLSearchParams(searchParams.toString());
                                newSearchParams.set('tab', tabIndex.toString())
                                setSearchParams(newSearchParams);
                            }}
                            switchForce={true}
                        >
                            <CardContent>
                                <AddressOverview d={data.payload}/>
                            </CardContent>
                            <CardContent>
                                <ViewAddressTxs
                                    address={address as string}
                                    totalTxs={data.payload.txn}
                                    pageNo={pageNo}
                                    pageSize={pageSize}
                                />
                            </CardContent>
                        </TabbedMainCard>
                    </Grid>
                </Grid>
            )}
        />
    );
};

export default ViewAddress;
